import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useNavigate, useLocation } from "react-router-dom";
import "./Authentication.css";
import { Helmet } from "react-helmet";
import SecondButton from "../../components/Elements/SecondButton/SecondButton";
import AuthenticationService from "../../services/AuthenticationService";
import { validateLoginInput, parseLoginError } from "../../utils/loginErrorHandler";
import { validateEmailInput, parseForgotPasswordError } from "../../utils/forgotPasswordErrorHandler";
import { validateResetPasswordInput, parseResetPasswordError } from "../../utils/resetPasswordErrorHandler";
import { validateRegisterInput, parseRegisterError } from "../../utils/registerErrorHandler";
import { validateVerificationCodeInput, parseVerificationCodeError } from "../../utils/verificationCodeErrorHandler";
import envelopeIcon from "../../assets/auth/envelope.png";
import lockIcon from "../../assets/auth/lock.png";
import userIcon from "../../assets/auth/user.png";
import hastag from "../../assets/auth/lock-hashtag.png";
import eye from "../../assets/auth/eye.png";
import eyeOff from "../../assets/auth/eye-crossed.png";
import AuthLoginForm from "../../components/Authentication/AuthLoginForm";
import AuthRegisterFlow from "../../components/Authentication/AuthRegisterFlow";
import AuthForgotPasswordFlow from "../../components/Authentication/AuthForgotPasswordFlow";
import {logEvent} from "../../analytics/analytics";

// Main authentication component handling all auth flows (login/register/forgot-password)
const Login = () => {
  // Router hooks for navigation
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile(770);
  const { setToken, setFirebaseToken } = useContext(AuthContext);

  // Core UI state
  const [showPassword, setShowPassword] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Flow-specific state
  const [forgotPasswordStage, setForgotPasswordStage] = useState("email");
  const [verificationStage, setVerificationStage] = useState("form");
  const [resetStatus, setResetStatus] = useState("sending");

  // Form input state
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");

  // UI feedback state
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Reset all form state to initial values
  const resetFormState = () => {
    setUsername("");
    setEmail("");
    setPassword("");
    setResetCode("");
    setNewPassword("");
    setError(null);
  };

  // Load saved credentials on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem("Email");
    const savedPassword = localStorage.getItem("Password");
    if (savedEmail) setEmail(savedEmail);
    if (savedPassword) setPassword(savedPassword);

    if (location.state?.isRegistering !== undefined) {
      setIsRegistering(location.state.isRegistering);
    }
  }, [location.state]);

  // Handle login submission
  const handleLogin = async () => {
    // setError("");
    const validationError = validateLoginInput(email, password);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      const response = await AuthenticationService.loginUser(email, password);
      setToken(response.access_token);
      setFirebaseToken(response.firebase_token);
      localStorage.setItem("token", response.access_token);
      localStorage.setItem("firebaseToken", response.firebase_token);

      if (rememberMe) {
        localStorage.setItem("Email", email);
        localStorage.setItem("Password", password);
      } else {
        localStorage.removeItem("Email");
        localStorage.removeItem("Password");
      }

      navigate("/account");
    } catch (err) {
      const message = parseLoginError(err.response);
      setError(message);
    }
  };

  // Handle registration verification email
  const handleSendVerificationEmail = async () => {
    setError("");
    const validationError = validateRegisterInput(username, email, password);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await AuthenticationService.sendVerificationCode(email);
      setVerificationStage("code");
    } catch (err) {
      const message = parseRegisterError(err);
      setError(message);
    }
  };

  // Handle registration code verification
  const handleVerifyRegistrationCode = async () => {
    setError("");
    const validationError = validateVerificationCodeInput(verificationCode);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await AuthenticationService.verifyEmailCode(email, verificationCode);
      await AuthenticationService.registerUser(email, password, username);
      setIsRegistering(false);
      setSuccessMessage("Your email has been successfully verified.");
      logEvent("auth", "register", `success`);
      resetFormState();
    } catch (err) {
      const message = parseVerificationCodeError(err);
      setError(message);
    }
  };

  // Handle forgot password email
  const handleSendResetEmail = async () => {
    setError("");
    const validationError = validateEmailInput(email);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await AuthenticationService.sendResetCode(email);
      setForgotPasswordStage("reset");
      setResetStatus("sent");
    } catch (err) {
      const message = parseForgotPasswordError(err);
      setError(message);
      setResetStatus("error");
    }
  };

  // Handle password reset
  const handleResetPassword = async () => {
    setError("");
    const validationError = validateResetPasswordInput(email, resetCode, newPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await AuthenticationService.resetPassword(email, resetCode, newPassword);
      setIsForgotPassword(false);
      setForgotPasswordStage("email");
      setResetCode("");
      setNewPassword("");
      setSuccessMessage("Your password has been successfully changed.");
    } catch (err) {
      const message = parseResetPasswordError(err);
      setError(message);
      setSuccessMessage(null);
    }
  };

  // Toggle between login/register modes
  const toggleRegisterMode = () => {
    setIsRegistering(!isRegistering);
    resetFormState();
    setIsForgotPassword(false);
    setForgotPasswordStage("email");
    setVerificationStage("form");
    setError(null);
  };

  const handleSetError = (message) => {
    setError(typeof message === "string" ? message : "Something went wrong.");
  };

  const handleClearError = () => {
    setError(null);
  };

  return (
    <div className="auth-container">
      <Helmet>
        <title>User Service - Sign In</title>
      </Helmet>

      {/* Welcome section with dynamic content */}
      <div className="welcome-section">
        <h1>{isRegistering ? "Join Telviora!" : "Welcome Back!"}</h1>
        <p className="description">
          To keep connected with us, please log in with your personal information. If you're new here, feel free to
          create an account and join our community.
        </p>
        {!isMobile && (
          <SecondButton className="toggle-register-desktop" onClick={toggleRegisterMode}>
            {isRegistering ? "Sign In" : "Register"}
          </SecondButton>
        )}
      </div>

      {/* Dynamic form rendering based on current auth flow */}
      <div className="sign-in-section">
        {!isRegistering && !isForgotPassword && (
          <AuthLoginForm
            envelopeIcon={envelopeIcon}
            lockIcon={lockIcon}
            eye={eye}
            eyeOff={eyeOff}
            email={email}
            password={password}
            rememberMe={rememberMe}
            showPassword={showPassword}
            error={error}
            setError={handleSetError}
            onFacebookError={handleSetError}
            isMobile={isMobile}
            onEmailChange={setEmail}
            onPasswordChange={setPassword}
            onRememberChange={setRememberMe}
            onTogglePassword={() => setShowPassword((prev) => !prev)}
            onForgotPassword={() => {
              setIsForgotPassword(true);
              setForgotPasswordStage("email");
              setError(null);
              setEmail("");
            }}
            onLogin={handleLogin}
            onSwitchToRegister={toggleRegisterMode}
            successMessage={successMessage}
            setSuccessMessage={setSuccessMessage}
            onCloseError={() => setError(null)}
          />
        )}

        {isRegistering && (
          <AuthRegisterFlow
            verificationStage={verificationStage}
            userIcon={userIcon}
            envelopeIcon={envelopeIcon}
            lockIcon={lockIcon}
            hastag={hastag}
            eye={eye}
            eyeOff={eyeOff}
            username={username}
            email={email}
            password={password}
            verificationCode={verificationCode}
            showPassword={showPassword}
            error={error}
            isMobile={isMobile}
            onUsernameChange={setUsername}
            onEmailChange={setEmail}
            onPasswordChange={setPassword}
            onVerificationCodeChange={setVerificationCode}
            onTogglePassword={() => setShowPassword((prev) => !prev)}
            onSendVerificationEmail={handleSendVerificationEmail}
            onVerifyRegistrationCode={handleVerifyRegistrationCode}
            onSwitchToLogin={toggleRegisterMode}
            onBackToForm={() => {
              setVerificationStage("form");
              setError(null);
            }}
            onCloseError={() => setError(null)}
          />
        )}

        {isForgotPassword && (
          <AuthForgotPasswordFlow
            forgotPasswordStage={forgotPasswordStage}
            resetStatus={resetStatus}
            envelopeIcon={envelopeIcon}
            lockIcon={lockIcon}
            hastag={hastag}
            eye={eye}
            eyeOff={eyeOff}
            email={email}
            resetCode={resetCode}
            newPassword={newPassword}
            showPassword={showPassword}
            error={error}
            onEmailChange={setEmail}
            onResetCodeChange={setResetCode}
            onNewPasswordChange={setNewPassword}
            onTogglePassword={() => setShowPassword((prev) => !prev)}
            onSendResetEmail={handleSendResetEmail}
            onResetPassword={handleResetPassword}
            onBackToLogin={() => {
              setIsForgotPassword(false);
              setForgotPasswordStage("email");
              resetFormState();
              setResetStatus("sending");
              setError(null);
            }}
            onCloseError={() => setError(null)}
          />
        )}
      </div>
    </div>
  );
};

export default Login;
